/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    CacheType,
    createObservableDataAction,
    getCatalogId,
    getCategoriesUrlSync,
    IAction,
    IActionContext,
    IActionInput,
    IAny,
    ICommerceApiSettings,
    ICreateActionContext,
    IGeneric,
    IRequestContext
} from '@msdyn365-commerce/core';
import { ICategoryPath, ICategoryUrl } from '@msdyn365-commerce/core-internal/dist/types/interfaces/ICategoryPathInterfaces';
import { getCategoryPathsAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import { CategoryPathLookup } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';

//----------------------------------------------------------
// @CUSTOM: Fix imports (absolute references)
//----------------------------------------------------------
import { QueryResultSettingsProxy, generateCacheKey, getSelectedProductIdFromActionInput } from '@msdyn365-commerce-modules/retail-actions';
//----------------------------------------------------------

/**
 * Category path input.
 */
export class GetCategoryPathsInput implements IActionInput {
    public readonly ChannelId: number;

    public readonly CatalogId: number;

    public readonly categoryPathLooksups: CategoryPathLookup[];

    public readonly apiSettings: ICommerceApiSettings;

    private readonly locale: string;

    private _requestContext: IRequestContext;

    public constructor(context: IRequestContext, categoryPathLooksups: CategoryPathLookup[]) {
        this._requestContext = context;
        this.ChannelId = context.apiSettings.channelId;
        this.CatalogId = this.getCatalog(context);           // @CUSTOM: Attempt to get the ID from the cookie if appropriate
        this.categoryPathLooksups = categoryPathLooksups;
        this.apiSettings = context.apiSettings;
        this.locale = context.locale;
    }

    public getCacheKey = () =>
        generateCacheKey(
            this._requestContext.apiSettings,
            { key: 'CategoryPathProductIds', value: this.categoryPathLooksups.map(categoryPath => categoryPath.ProductId) },
            { key: 'CatalogId', value: this.CatalogId },
            { key: 'locale', value: this.locale }
        );

    public getCacheObjectType = () => 'CategoryPath';

    public dataCacheType = (): CacheType => 'request';

    /**
     * Retrieves locale to use for the current API call.
     * @returns A string which represents locale.
     */
    public getLocale = (): string => this.locale;

    //----------------------------------------------------------
    // @CUSTOM
    //----------------------------------------------------------
    private getCatalog(context: IRequestContext) {
        const COOKIE_NAME = 'd365_catalog';

        // Only do something if the user is logged in
        if (context.user && (context.user.isB2b || context.user.isOBORequest)) {

            // Attempt to get catalog values from both the cookie and URL
            const cookieId = context.cookies.get<string>(COOKIE_NAME);      // This may even work on the server
            const urlId = context.query && context.query.catalogid;         // This should work on the server

            // If catalog is in the URL, that takes precedence. Update the cookie.
            if (!urlId && cookieId?.value) {
                return +cookieId.value;
            }
        }

        return getCatalogId(context);
    }
}

/**
 * Creates the input required to make the getCategoryPath retail api call.
 * @param inputData
 */
export const createGetCategoryPathsInput = (inputData: ICreateActionContext<IGeneric<IAny>>): IActionInput => {
    const productId = getSelectedProductIdFromActionInput(inputData);
    if (productId) {
        return new GetCategoryPathsInput(inputData.requestContext, [{ ProductId: +productId }]);
    }
    throw new Error('Unable to create SelectedVariantInput, no productId found on module config or query');
};

/**
 * Calls the Retail API and returns the category path for a product.
 * @param input
 * @param ctx
 */
export async function getCategoryPathsAction(input: GetCategoryPathsInput, ctx: IActionContext): Promise<ICategoryUrl[]> {
    const categoryPathResults = await getCategoryPathsAsync(
        {
            callerContext: ctx,
            queryResultSettings: QueryResultSettingsProxy.getPagingFromInputDataOrDefaultValue(ctx)
        },
        input.ChannelId,
        input.CatalogId,
        input.categoryPathLooksups
    );
    const categoryPath = categoryPathResults[0].CategoryPath && categoryPathResults[0].CategoryPath[0];
    const categoryUrl = getCategoriesUrlSync(<ICategoryPath>categoryPath, ctx);
    if (categoryUrl) {
        /* Do not return the root category of the product*/
        return categoryUrl.slice(1);
    }
    return [];
}

export const getCategoryPathsActionDataAction = createObservableDataAction({
    id: '@msdyn365-commerce-modules/retail-actions/get-category-paths',
    action: <IAction<ICategoryUrl[]>>getCategoryPathsAction,
    input: createGetCategoryPathsInput
});

export default getCategoryPathsActionDataAction;
